import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { getModulePermissions } from "../../utils/permissionUtils";
import { exportSubscriptions, fetchSubscriptions } from "../services/PaymentsandTransactions";

const SubscriptionsListPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [exportType, setExportType] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    status: "",
    platform: "",
    userId: "",
    from: "",
    to: "",
    search: "",
    itemsPerPage: 10,
    currentPage: 1,
  });

  const [total, setTotal] = useState(0);

  const { shouldCheckPermissions, canView } = getModulePermissions("subscriptions");

  // Fetch subscriptions
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.currentPage,
        limit: filters.itemsPerPage,
        status: filters.status,
        platform: filters.platform,
        userId: filters.userId,
        from: filters.from,
        to: filters.to,
        search: filters.search,
      };
      const res = await fetchSubscriptions(params);
      setSubscriptions(res?.data?.data);
      setTotal(res?.data?.total);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to fetch subscriptions");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const totalPages = Math.ceil(total / filters.itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setFilters((prev) => ({ ...prev, currentPage: page }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      itemsPerPage: parseInt(e.target.value, 10),
      currentPage: 1,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      currentPage: 1,
    }));
  };

  const getPaginationRange = () => {
    let start = Math.max(1, filters.currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const isSelected = (id) => selectedItems.includes(id);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedItems(subscriptions.map((item) => item._id));
    else setSelectedItems([]);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    if (!exportType) return;

    try {
      setExportLoading(true);
      const res = await exportSubscriptions({
        status: filters.status,
        platform: filters.platform,
        search: filters.search,
        from: filters.from,
        to: filters.to,
        format: exportType === "excel" ? "xlsx" : "csv",
      });

      const fileType =
        exportType === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv";

      const fileName =
        exportType === "excel" ? "subscriptions.xlsx" : "subscriptions.csv";

      const blob = new Blob([res.data], { type: fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
    } catch (err) {
      console.error(err);
      setErrorMessage("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

      {/* Filters and Export */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{ fontSize: "16px", borderRadius: "7px" }}
            value={filters.itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <select
            name="status"
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{ fontSize: "16px", borderRadius: "7px" }}
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            name="platform"
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{ fontSize: "16px", borderRadius: "7px" }}
            value={filters.platform}
            onChange={handleFilterChange}
          >
            <option value="">All Platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
            <option value="web">Web</option>
          </select>

          {/* Search */}
          <form className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value, currentPage: 1 }))
              }
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  setFilters((prev) => ({
                    ...prev,
                    search: "",
                    currentPage: 1,
                  }));
                }
              }}
            />
            <Icon icon="ion:search-outline" className="icon" />
            {filters.search && (
              <button
                onClick={() => setFilters((prev) => ({ ...prev, search: "", currentPage: 1 }))}
                style={{
                  position: "absolute",
                  insetInlineEnd: "1.9375rem",
                  top: "40%",
                  transform: "translateY(-50%)",
                  fontSize: "1.125rem",
                  color: "var(--black)", // Corrected from `color: var(--black)` which was outside the object
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          {/* Date Filters */}
          <div className="col-md-2" style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={filters.from ? new Date(filters.from) : null}
              onChange={(date) =>
                setFilters({ ...filters, from: date ? date.toISOString().split("T")[0] : "" })
              }
              className="form-control"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />
            {filters.from && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, from: "" })}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          <div className="col-md-2" style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={filters.to ? new Date(filters.to) : null}
              onChange={(date) =>
                setFilters({ ...filters, to: date ? date.toISOString().split("T")[0] : "" })
              }
              className="form-control"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
            />
            {filters.to && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, to: "" })}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          {/* Export */}
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              value={exportType}
              onChange={(e) => setExportType(e.target.value)}
            >
              <option value="">Export as</option>
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
            </select>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={!exportType || exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        className="form-check-input radius-4 border input-form-dark"
                        type="checkbox"
                        name="checkbox"
                        id="selectAll"
                        onChange={handleSelectAll}
                        checked={selectedItems.length === subscriptions.length && subscriptions.length > 0}
                      />
                    </div>
                  </div>
                </th>
                <th>User</th>
                <th>Email</th>
                <th>Product</th>
                <th>Platform</th>
                <th>Status</th>
                <th>Auto Renew</th>
                <th>Start Date</th>
                <th>Expiry</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : subscriptions.length > 0 ? (
                subscriptions.map((sub) => (
                  <tr key={sub._id}>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input
                            className="form-check-input radius-4 border border-neutral-400"
                            type="checkbox"
                            name="checkbox"
                            value={sub._id}
                            checked={isSelected(sub._id)}
                            onChange={() => handleSelectItem(sub._id)}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{sub.user?.fullName || "N/A"}</td>
                    <td>{sub.user?.email || "N/A"}</td>
                    <td>{sub.productId}</td>
                    <td>{sub.platform}</td>
                    <td>{sub.status}</td>
                    <td>{sub.autoRenew ? "Yes" : "No"}</td>
                    <td>{sub.startDate ? moment(sub.startDate).format("DD-MM-YYYY") : "-"}</td>
                    <td>{sub.expiryDate ? moment(sub.expiryDate).format("DD-MM-YYYY") : "-"}</td>
                    <td className="d-flex gap-2">
                      {(!shouldCheckPermissions || canView) && (
                        <button
                          title="View"
                          onClick={() => navigate(`/subscriptions/view/${sub._id}`)}
                          className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="majesticons:eye-line" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center">
                    No Subscriptions Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {(filters.currentPage - 1) * filters.itemsPerPage + 1} to{" "}
            {Math.min(filters.currentPage * filters.itemsPerPage, total)} of {total} entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${filters.currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={() => handlePageChange(filters.currentPage - 1)}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>

            {getPaginationRange().map((p) => (
              <li key={p} className="page-item">
                <Link
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${filters.currentPage === p ? "bg-primary-600 text-white" : "bg-neutral-200"
                    }`}
                  to="#"
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </Link>
              </li>
            ))}

            <li className={`page-item ${filters.currentPage === totalPages ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={() => handlePageChange(filters.currentPage + 1)}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsListPage;
